import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["preserveTabOrder", "portalElement", "portalRef", "portal"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useContext, useState, useEffect } from 'react';
import { getDocument } from 'ariakit-utils/dom';
import { isFocusEventOutside } from 'ariakit-utils/events';
import { getNextTabbable, getPreviousTabbable, restoreFocusIn, disableFocusIn } from 'ariakit-utils/focus';
import { useForkRef, useSafeLayoutEffect, useWrapElement } from 'ariakit-utils/hooks';
import { setRef, queueMicrotask } from 'ariakit-utils/misc';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { createPortal } from 'react-dom';
import { PortalContext } from './portal-context.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { FocusTrap } from '../focus-trap/focus-trap.js';

function getRootElement(element) {
  return getDocument(element).body;
}

function getPortalElement(element, portalElement) {
  if (!portalElement) {
    return getDocument(element).createElement("div");
  }

  if (typeof portalElement === "function") {
    return portalElement(element);
  }

  return portalElement;
}

function getRandomId(prefix) {
  if (prefix === void 0) {
    prefix = "id";
  }

  return "" + (prefix ? prefix + "-" : "") + Math.random().toString(36).substr(2, 6);
}

function queueFocus(element) {
  queueMicrotask(function () {
    element == null ? void 0 : element.focus();
  });
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element using `ReactDOM.createPortal`.
 * @see https://ariakit.org/components/portal
 * @example
 * ```jsx
 * const props = usePortal();
 * <Role {...props}>Content</Role>
 * ```
 */


var usePortal = createHook(function (_ref) {
  var preserveTabOrder = _ref.preserveTabOrder,
      portalElement = _ref.portalElement,
      portalRef = _ref.portalRef,
      _ref$portal = _ref.portal,
      portal = _ref$portal === void 0 ? true : _ref$portal,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null);
  var refProp = useForkRef(ref, props.ref);
  var context = useContext(PortalContext);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      portalNode = _useState2[0],
      setPortalNode = _useState2[1];

  var beforeOutsideRef = useRef(null);
  var beforeInsideRef = useRef(null);
  var afterInsideRef = useRef(null);
  var afterOutsideRef = useRef(null); // Create the portal node and attach it to the DOM.

  useSafeLayoutEffect(function () {
    var element = ref.current;

    if (!element || !portal) {
      setPortalNode(null);
      return;
    }

    var portalEl = getPortalElement(element, portalElement); // TODO: Warn about portals as the document.body element.

    if (!portalEl) {
      setPortalNode(null);
      return;
    }

    var isPortalInDocument = portalEl.isConnected;

    if (!isPortalInDocument) {
      var rootElement = context || getRootElement(element);
      rootElement.appendChild(portalEl);
    } // If the portal element doesn't have an id already, set one.


    if (!portalEl.id) {
      // Use the element's id so rendering <Portal id="some-id" /> will
      // produce predictable results.
      portalEl.id = element.id ? element.id + "-portal" : getRandomId();
    } // Set the internal portal node state and the portalRef prop.


    setPortalNode(portalEl);
    setRef(portalRef, portalEl); // If the portal element was already in the document, we don't need to
    // remove it when the element is unmounted, so we just return.

    if (isPortalInDocument) return; // Otherwise, we need to remove the portal from the DOM.

    return function () {
      portalEl.remove();
      setRef(portalRef, null);
    };
  }, [portal, portalElement, context, portalRef]); // When preserveTabOrder is true, make sure elements inside the portal
  // element are tabbable only when the portal has already been focused,
  // either by tabbing into a focus trap element outside or using the mouse.

  useEffect(function () {
    if (!portalNode) return;
    if (!preserveTabOrder) return;
    var raf = 0;

    var onFocus = function onFocus(event) {
      if (isFocusEventOutside(event)) {
        var focusing = event.type === "focusin";
        if (focusing) return restoreFocusIn(portalNode); // Wait for the next frame to allow tabindex changes after the focus
        // event.

        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          disableFocusIn(portalNode, true);
        });
      }
    }; // Listen to the event on the capture phase so they run before the focus
    // trap elements onFocus prop is called.


    portalNode.addEventListener("focusin", onFocus, true);
    portalNode.addEventListener("focusout", onFocus, true);
    return function () {
      portalNode.removeEventListener("focusin", onFocus, true);
      portalNode.removeEventListener("focusout", onFocus, true);
    };
  }, [portalNode, preserveTabOrder]);
  props = useWrapElement(props, function (element) {
    element =
    /*#__PURE__*/
    // While the portal node is not in the DOM, we need to pass the
    // current context to the portal context, otherwise it's going to
    // reset to the body element on nested portals.
    jsx(PortalContext.Provider, {
      value: portalNode || context,
      children: element
    });
    if (!portal) return element;

    if (!portalNode) {
      // If the element should be rendered within a portal, but the portal
      // node is not yet in the DOM, we'll return an empty div element. We
      // assign the id to the element so we can use it to set the portal id
      // later on. We're using position: fixed here so that the browser
      // doesn't add margin to the element when setting gap on a parent
      // element.
      return /*#__PURE__*/jsx("span", {
        ref: refProp,
        id: props.id,
        style: {
          position: "fixed"
        }
      });
    }

    element = /*#__PURE__*/jsxs(Fragment, {
      children: [preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: beforeInsideRef,
        onFocus: function onFocus(event) {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(getNextTabbable());
          } else {
            queueFocus(beforeOutsideRef.current);
          }
        }
      }), element, preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: afterInsideRef,
        onFocus: function onFocus(event) {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(getPreviousTabbable());
          } else {
            queueFocus(afterOutsideRef.current);
          }
        }
      })]
    });

    if (portalNode) {
      element = /*#__PURE__*/createPortal(element, portalNode);
    }

    element = /*#__PURE__*/jsxs(Fragment, {
      children: [preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: beforeOutsideRef,
        onFocus: function onFocus(event) {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(beforeInsideRef.current);
          } else {
            queueFocus(getPreviousTabbable());
          }
        }
      }), preserveTabOrder &&
      /*#__PURE__*/
      // We're using position: fixed here so that the browser doesn't
      // add margin to the element when setting gap on a parent element.
      jsx("span", {
        "aria-owns": portalNode == null ? void 0 : portalNode.id,
        style: {
          position: "fixed"
        }
      }), element, preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: afterOutsideRef,
        onFocus: function onFocus(event) {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(afterInsideRef.current);
          } else {
            queueFocus(getNextTabbable());
          }
        }
      })]
    });
    return element;
  }, [portalNode, context, portal, props.id, preserveTabOrder]);
  props = _objectSpread(_objectSpread({}, props), {}, {
    ref: refProp
  });
  return props;
});
/**
 * A component that renders an element using `ReactDOM.createPortal`.
 * @see https://ariakit.org/components/portal
 * @example
 * ```jsx
 * <Portal>Content</Portal>
 * ```
 */

var Portal = createComponent(function (props) {
  var htmlProps = usePortal(props);
  return createElement("div", htmlProps);
});
export { Portal, usePortal };